import { Button } from "@fluentui/react-components";
import { PersonFeedback24Regular } from "@fluentui/react-icons";
import { useStyle } from "./style";
import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";
import { useCallback, useState } from "react";
import { AgentState, useAgentStore } from "@/common/stores/persistingStores/agentPermissions";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import FeedbackForm from "./FeedbackForm";
import { FeedbackFormValues } from "./types";
import { useSentryAppContext } from "@/hooks/sentry";
import { useMessage } from "@/common/messages";
import { captureError } from "@/common/helpers/sentryHelper";

const FeedbackButton = () => {
	const intl = useIntl();

	const classes = useStyle();

	const { getAppContext } = useSentryAppContext();

	const { show: showNotification } = useMessage();

	const [isFormOpen, setIsFormOpen] = useState(false);

	const agentFullName = useAgentStore((state: AgentState) => state.fullName);

	const setFormOpen = (open: boolean) => setIsFormOpen(open);

	const handleSubmitFeedback = useCallback(
		async (feedbackData: FeedbackFormValues) => {
			const eventHint: Sentry.EventHint = {};

			try {
				if (feedbackData.screenshot) {
					const arrayBuffer = await feedbackData.screenshot.arrayBuffer();
					const uint8Array = new Uint8Array(arrayBuffer);

					eventHint.attachments = [
						{
							filename: feedbackData.screenshot.name,
							data: uint8Array,
						},
					];
				}

				const eventId = Sentry.captureMessage("Feedback submitted", {
					level: "log",
					extra: {
						...getAppContext(),
					},
				});

				Sentry.captureFeedback(
					{
						message: feedbackData.description,
						email: feedbackData.email,
						name: feedbackData.name,
						associatedEventId: eventId,
					},
					eventHint
				);

				showNotification("global", {
					intent: "success",
					text: intl.formatMessage({ id: "Feedback.Form.Success" }),
				});

				setFormOpen(false);
			} catch (error: any) {
				captureError(error, "Error submitting feedback");

				showNotification("global", {
					intent: "error",
					text: intl.formatMessage({ id: "Feedback.Form.Error" }),
				});
			}
		},
		[getAppContext, showNotification]
	);

	return (
		<>
			<Button
				appearance="transparent"
				className={classes.button}
				icon={<PersonFeedback24Regular />}
				children={intl.formatMessage({ id: "Account.Feedback" })}
				onClick={() => setFormOpen(true)}
			/>
			{isFormOpen && (
				<FeedbackForm
					onClose={() => setFormOpen(false)}
					onSubmit={handleSubmitFeedback}
					initialValues={{
						email: Lockr.get(StorageKeys.SIP) || "",
						name: agentFullName,
						description: "",
						screenshot: undefined,
					}}
				/>
			)}
		</>
	);
};

export default FeedbackButton;
